export const DEAL_TYPES = {
  PRICE_ADVANTAGE: 'price_advantage',
  MBW_ADVANTAGE: 'mbw_advantage',
};

export const DEAL_PRODUCT_CODE_MAX_LENGTH = 150;

export const MBW_DEFAULT_BY_BENEFIT = {
  discount_5: 15,
  discount_10: 25,
  discount_15: 35,
  discount_20: 45,
  discount_30: 65,
};

export const BENEFIT_OPTIONS = [
  { type: 'discount_5', label: '-5€' },
  { type: 'discount_10', label: '-10€' },
  { type: 'discount_15', label: '-15€' },
  { type: 'discount_20', label: '-20€' },
  { type: 'discount_30', label: '-30€' },
  { type: 'two_for_one', label: '2für1', yellow: true },
  { type: 'goodie', label: '+ Goodie', yellow: true },
  { type: 'select', label: 'Select', yellow: true },
  { type: 'special', label: 'Special', yellow: true },
  { type: 'exklusiv', label: 'Exklusiv', yellow: true },
];

const DISCOUNT_BENEFIT_TYPES = new Set([
  'discount_5',
  'discount_10',
  'discount_15',
  'discount_20',
  'discount_30',
]);

const BENEFIT_DISCOUNT_LABEL = {
  discount_5: '-5€',
  discount_10: '-10€',
  discount_15: '-15€',
  discount_20: '-20€',
  discount_30: '-30€',
};

const MAIN_BADGE_COLORS = {
  discountDark: { bg: '#111111', color: '#ffffff' },
  yellow: { bg: '#ffcc00', color: '#111111' },
  free: { bg: '#111111', color: '#ffcc00' },
};

export const MBW_ALTERNATE_BADGE_COLORS = {
  bg: '#111111',
  color: '#ffffff',
};

export const parsePriceEur = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(String(value).replace(',', '.').trim());
  return Number.isFinite(num) ? num : null;
};

const parseDealPriceEur = (value) => {
  const parsed = parsePriceEur(value);
  return parsed == null ? null : Math.max(0, parsed);
};

export const normalizeDealType = (value) => (
  value === DEAL_TYPES.MBW_ADVANTAGE ? DEAL_TYPES.MBW_ADVANTAGE : DEAL_TYPES.PRICE_ADVANTAGE
);

export const normalizeBenefitType = (value) => {
  if (!value) return null;
  const normalized = String(value).toLowerCase();
  return BENEFIT_OPTIONS.some((option) => option.type === normalized) ? normalized : null;
};

export const isDiscountBenefitType = (benefitType) => DISCOUNT_BENEFIT_TYPES.has(benefitType);

export const isYellowBenefitType = (benefitType) => (
  BENEFIT_OPTIONS.some((option) => option.type === benefitType && option.yellow)
);

export const isMbwBenefitWithOptionalPrices = (benefitType) => isYellowBenefitType(benefitType);

export const getMbwDefaultForBenefit = (benefitType) => (
  MBW_DEFAULT_BY_BENEFIT[benefitType] ?? null
);

export const getDiscountLabelForBenefit = (benefitType) => (
  BENEFIT_DISCOUNT_LABEL[benefitType] || null
);

export const parseExtraTags = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.slice(0, 2);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.slice(0, 2) : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

export const resolveLegacyExtraTags = (special) => {
  const existing = parseExtraTags(special?.extraTags ?? special?.extra_tags);
  if (existing.length) {
    return existing.map((tag) => ({
      label: tag.label,
      bgColor: tag.bgColor || tag.bg || '#111111',
      textColor: tag.textColor || tag.color || '#ffffff',
    }));
  }
  const category = String(special?.category || '').trim();
  if (!category) return [];
  return [{
    label: category,
    bgColor: special?.categoryColor || special?.category_color || '#26a69a',
    textColor: '#ffffff',
  }];
};

export const resolveMainBadge = (special) => {
  if (!special) return null;

  const dealType = normalizeDealType(special.dealType ?? special.deal_type);
  const benefitType = normalizeBenefitType(special.benefitType ?? special.benefit_type);

  if (dealType === DEAL_TYPES.MBW_ADVANTAGE && benefitType) {
    if (isDiscountBenefitType(benefitType)) {
      return {
        text: getDiscountLabelForBenefit(benefitType),
        ...MAIN_BADGE_COLORS.discountDark,
      };
    }
    if (isYellowBenefitType(benefitType)) {
      const label = BENEFIT_OPTIONS.find((option) => option.type === benefitType)?.label;
      if (label) {
        return { text: label, ...MAIN_BADGE_COLORS.yellow };
      }
    }
  }

  if (dealType === DEAL_TYPES.PRICE_ADVANTAGE) {
    const regular = parseDealPriceEur(special.regularPrice ?? special.regular_price);
    const price = parseDealPriceEur(special.price);
    if (regular != null && regular > 0 && price != null && price <= 0) {
      return { text: 'free', ...MAIN_BADGE_COLORS.free };
    }
  }

  return null;
};

const MBW_LEGACY_PREFIX_RE = /^Ab(?:\s+Mindestbestellwert)?\s+([\d.,]+)\s*€\.?\s*/i;
const MBW_NOTE_PREFIX_RE = /^([\d.,]+)\s*€\s+MBW\.?\s*/i;

export const formatMbwEuroAmount = (minimumOrderValue) => {
  const mbw = parsePriceEur(minimumOrderValue);
  if (mbw == null || mbw <= 0) return '';
  return Number.isInteger(mbw) ? String(mbw) : mbw.toFixed(2).replace('.', ',');
};

export const extractMbwFromProductCode = (text = '') => {
  const value = String(text || '').trim();
  const legacyMatch = value.match(MBW_LEGACY_PREFIX_RE);
  if (legacyMatch) return parsePriceEur(legacyMatch[1]);
  const noteMatch = value.match(MBW_NOTE_PREFIX_RE);
  if (noteMatch) return parsePriceEur(noteMatch[1]);
  return null;
};

export const resolveMinimumOrderValue = (special) => {
  const fromField = parsePriceEur(
    special?.minimumOrderValue ?? special?.minimum_order_value,
  );
  if (fromField != null && fromField > 0) return fromField;
  return extractMbwFromProductCode(special?.productCode);
};

export const buildMbwNoteText = (minimumOrderValue) => {
  const formatted = formatMbwEuroAmount(minimumOrderValue);
  if (!formatted) return '';
  return `${formatted}€ MBW`;
};

export const buildMbwShortBadgeText = (minimumOrderValue) => {
  const formatted = formatMbwEuroAmount(minimumOrderValue);
  if (!formatted) return '';
  return `Ab ${formatted}€`;
};

export const buildMbwVoidDisclaimerText = (minimumOrderValue) => {
  const formatted = formatMbwEuroAmount(minimumOrderValue);
  if (!formatted) return '';
  return `Ab einem Bestellwert von ${formatted}€`;
};

export const buildConditionText = (special) => {
  if (!special) return '';
  const partnerNote = String(special.productCode || '').trim();
  const dealType = normalizeDealType(special?.dealType ?? special?.deal_type);
  if (dealType === DEAL_TYPES.MBW_ADVANTAGE) {
    const noteText = buildMbwNoteText(resolveMinimumOrderValue(special));
    if (!noteText) return partnerNote;
    if (!partnerNote) return noteText;
    return `${noteText}. ${partnerNote}`;
  }
  return partnerNote;
};

export const normalizeHideCardPrices = (value) => Boolean(
  value === true
  || value === 1
  || value === '1'
  || String(value).toLowerCase() === 'true',
);

export const shouldHideCardPrices = (special) => {
  if (!normalizeHideCardPrices(special?.hideCardPrices ?? special?.hide_card_prices)) {
    return false;
  }
  const dealType = normalizeDealType(special?.dealType ?? special?.deal_type);
  if (dealType !== DEAL_TYPES.PRICE_ADVANTAGE) return false;
  const price = parseDealPriceEur(special?.price);
  return price != null && price <= 0;
};

/** Yellow MBW benefits: show card prices only when optional fields were filled in. */
export const hasMbwOptionalCardPrices = (special) => {
  const dealType = normalizeDealType(special?.dealType ?? special?.deal_type);
  const benefitType = normalizeBenefitType(special?.benefitType ?? special?.benefit_type);
  if (dealType !== DEAL_TYPES.MBW_ADVANTAGE || !isYellowBenefitType(benefitType)) {
    return false;
  }
  const regular = parseDealPriceEur(special?.regularPrice ?? special?.regular_price);
  const price = parseDealPriceEur(special?.price);
  return (regular != null && regular > 0) || (price != null && price > 0);
};

export const getDisplayPriceProps = (special) => {
  const dealType = normalizeDealType(special?.dealType ?? special?.deal_type);
  const benefitType = normalizeBenefitType(special?.benefitType ?? special?.benefit_type);

  if (dealType === DEAL_TYPES.MBW_ADVANTAGE && isDiscountBenefitType(benefitType)) {
    return {
      oldPrice: null,
      newPrice: null,
      discount: getDiscountLabelForBenefit(benefitType),
      isFree: false,
    };
  }

  if (dealType === DEAL_TYPES.MBW_ADVANTAGE && isYellowBenefitType(benefitType) && !hasMbwOptionalCardPrices(special)) {
    const parsedPrice = parseDealPriceEur(special?.price);
    if (benefitType === 'goodie' && (parsedPrice == null || parsedPrice <= 0)) {
      return {
        oldPrice: null,
        newPrice: null,
        discount: '',
        isFree: true,
      };
    }
    return {
      oldPrice: null,
      newPrice: null,
      discount: '',
      isFree: false,
    };
  }

  if (shouldHideCardPrices(special)) {
    return {
      oldPrice: null,
      newPrice: null,
      discount: '',
      isFree: false,
    };
  }

  const regular = special?.regularPrice ?? special?.regular_price;
  const price = special?.price;
  const parsedPrice = parseDealPriceEur(price);
  return {
    oldPrice: regular ?? null,
    newPrice: price ?? null,
    discount: '',
    isFree: parsedPrice != null
      && parsedPrice <= 0
      && (
        dealType === DEAL_TYPES.PRICE_ADVANTAGE
        || (dealType === DEAL_TYPES.MBW_ADVANTAGE && isYellowBenefitType(benefitType))
      ),
  };
};

export const getDealCardProps = (special) => {
  const mainBadge = resolveMainBadge(special);
  const extraTags = resolveLegacyExtraTags(special);
  const priceProps = getDisplayPriceProps(special);
  const dealType = normalizeDealType(special?.dealType ?? special?.deal_type);
  const minimumOrderValue = dealType === DEAL_TYPES.MBW_ADVANTAGE
    ? resolveMinimumOrderValue(special)
    : null;
  const badgeAlternateText = mainBadge && minimumOrderValue
    ? buildMbwShortBadgeText(minimumOrderValue)
    : '';
  const mbwDisclaimerText = minimumOrderValue
    ? buildMbwVoidDisclaimerText(minimumOrderValue)
    : '';

  return {
    mainBadge,
    extraTags,
    badgeText: mainBadge?.text || '',
    badgeAlternateText,
    badgeAlternateColor: badgeAlternateText ? MBW_ALTERNATE_BADGE_COLORS.color : '',
    badgeAlternateBackgroundColor: badgeAlternateText ? MBW_ALTERNATE_BADGE_COLORS.bg : '',
    badgeColor: mainBadge?.bg || '#111111',
    badgeTextColor: mainBadge?.color || '#ffffff',
    conditionText: buildConditionText(special),
    mbwDisclaimerText,
    oldPrice: priceProps.oldPrice,
    newPrice: priceProps.newPrice,
    discount: priceProps.discount,
    isFree: priceProps.isFree,
  };
};

export const BENEFIT_YELLOW_OPTIONS = BENEFIT_OPTIONS.filter((option) => option.yellow);
export const BENEFIT_DISCOUNT_OPTIONS = BENEFIT_OPTIONS.filter((option) => !option.yellow);

export const benefitPillStyle = (option, selected) => {
  if (option.yellow) {
    return { background: '#ffcc00', borderColor: '#ffcc00', color: '#111111' };
  }
  if (selected) {
    return { background: '#111111', borderColor: '#111111', color: '#ffffff' };
  }
  return { background: '#f2f2f2', borderColor: '#e0e0e0', color: '#111111' };
};
